import React, { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { chunkArray, highlightAll, calc } from "../../utils";
import { NumericFormat } from "react-number-format";
import { Box, Card, CardContent, Typography, Grid, TextField, Divider, InputAdornment, MenuItem, Button } from "@mui/material";
import { selectInputValues, selectLabeledInputValues, updateInput } from "./inputsSlice";
import { loadInitial } from "./outputsSlice";

export const Inputs = () => {
	const dispatch = useDispatch();
	const view = useSelector(state => state.channelMix.settings.view);
	const { rate, stay, wage, minutes, breakfast, amenities, cleaning, laundry, utilities, points, comm } = useSelector(selectLabeledInputValues);
	const fullDays = useSelector(state => state.channelMix.outputs.fullDays);
	const lightDays = useSelector(state => state.channelMix.outputs.lightDays);
	const chunked = chunkArray(useSelector(selectInputValues), 9);
	const inputs = [chunkArray(chunked[0], 3), chunkArray(chunked[1], 2)];

	const onChange = (e, id) => {
		const number = parseFloat(e.target.value.replace(/,/g, "") || 0);
		dispatch(updateInput({id, changes: {value: number || e.target.value}}));
	};

	const SelectProps = {
		MenuProps: {
			disableScrollLock: true
		}
	};

	const runSubmit = () => {
		const rLabor = (minutes/60)*wage;
		const lLabor = wage/10;
		const fFee = rate*0.09;
		const fFee35 = (rate*0.65)*0.09;
		const fFee50 = (rate*0.5)*0.09;
		const cp = rate*0.05;
		const gdsComm = rate*0.1;
		const bComm = rate*0.14;
		const eComm = rate*0.156;
		const gdsFee = 7.7/stay;
		const tFee = 4/stay;
		const ccFee = rate*0.02;

		const wrLabor = (day) => fullDays[day] ? wage*(40/60) : 0.00;
		const ltLabor = (day) => lightDays[day] ? wage*0.167 : 0.00;

		const cpRate = points === "Yes" ? 0.05 : 0;
		const commRate = comm === "Yes" ? 0.1 : 0;
		const wlLabor = wage*0.167;
		const wcp = rate*cpRate;
		const wcomm = rate*commRate;

		const init = [
			{id: 0, type: "cost", expense: "Room attendant labor", noncp: rLabor, cp: rLabor, gdscp: rLabor, gdsnoncp: rLabor, booking: rLabor, expedia: rLabor, op35: rLabor, op50: rLabor},
			{id: 1, type: "cost", expense: "Breakfast", noncp: breakfast, cp: breakfast, gdscp: breakfast, gdsnoncp: breakfast, booking: breakfast, expedia: breakfast, op35: breakfast, op50: breakfast},
			{id: 2, type: "cost", expense: "Utilities", noncp: utilities, cp: utilities, gdscp: utilities, gdsnoncp: utilities, booking: utilities, expedia: utilities, op35: utilities, op50: utilities},
			{id: 3, type: "cost", expense: "Room amenities", noncp: amenities, cp: amenities, gdscp: amenities, gdsnoncp: amenities, booking: amenities, expedia: amenities, op35: amenities, op50: amenities},
			{id: 4, type: "cost", expense: "Laundry labor", noncp: lLabor, cp: lLabor, gdscp: lLabor, gdsnoncp: lLabor, booking: lLabor, expedia: lLabor, op35: lLabor, op50: lLabor},
			{id: 5, type: "cost", expense: "Cleaning supplies", noncp: cleaning, cp: cleaning, gdscp: cleaning, gdsnoncp: cleaning, booking: cleaning, expedia: cleaning, op35: cleaning, op50: cleaning},
			{id: 6, type: "cost", expense: "Laundry chemicals", noncp: laundry, cp: laundry, gdscp: laundry, gdsnoncp: laundry, booking: laundry, expedia: laundry, op35: laundry, op50: laundry},
			{id: 7, type: "cost", expense: "Franchise Fees<sup>1</sup>", noncp: fFee, cp: fFee, gdscp: fFee, gdsnoncp: fFee, booking: fFee, expedia: fFee, op35: fFee35, op50: fFee50},
			{id: 8, type: "cost", expense: "Choice Privileges", noncp: 0.00, cp: cp, gdscp: cp, gdsnoncp: 0.00, booking: 0.00, expedia: 0.00, op35: 0.00, op50: 0.00},
			{id: 9, type: "cost", expense: "Commission<sup>2</sup>", noncp: 0.00, cp: 0.00, gdscp: gdsComm, gdsnoncp: gdsComm, booking: bComm, expedia: eComm, op35: 0.00, op50: 0.00},
			{id: 10, type: "cost", expense: "GDS Fee<sup>3</sup>", noncp: 0.00, cp: 0.00, gdscp: gdsFee, gdsnoncp: gdsFee, booking: 0.00, expedia: 0.00, op35: gdsFee, op50: gdsFee},
			{id: 11, type: "cost", expense: "Transaction fee<sup>4</sup>", noncp: 0.00, cp: 0.00, gdscp: 0.00, gdsnoncp: 0.00, booking: tFee, expedia: tFee, op35: 0.00, op50: 0.00},
			{id: 12, type: "cost", expense: "Credit Card processing fee<sup>5</sup>", noncp: ccFee, cp: ccFee, gdscp: ccFee, gdsnoncp: ccFee, booking: ccFee, expedia: ccFee, op35: ccFee, op50: ccFee},

			{id: 13, type: "weekly", expense: "Room attendant labor", sun: wrLabor("sun"), mon: wrLabor("mon"), tue: wrLabor("tue"), wed: wrLabor("wed"), thu: wrLabor("thu"), fri: wrLabor("fri"), sat: wrLabor("sat"), total: calc(wrLabor)},
			{id: 14, type: "weekly", expense: "Light Touch labor", sun: ltLabor("sun"), mon: ltLabor("mon"), tue: ltLabor("tue"), wed: ltLabor("wed"), thu: ltLabor("thu"), fri: ltLabor("fri"), sat: ltLabor("sat"), total: calc(ltLabor)},
			{id: 15, type: "weekly", expense: "Breakfast", sun: breakfast, mon: breakfast, tue: breakfast, wed: breakfast, thu: breakfast, fri: breakfast, sat: breakfast, total: breakfast*7},
			{id: 16, type: "weekly", expense: "Utilities", sun: utilities, mon: utilities, tue: utilities, wed: utilities, thu: utilities, fri: utilities, sat: utilities, total: utilities*7},
			{id: 17, type: "weekly", expense: "Room amenities", sun: amenities, mon: amenities, tue: amenities, wed: amenities, thu: amenities, fri: amenities, sat: amenities, total: 0.00},
			{id: 18, type: "weekly", expense: "Laundry labor", sun: wlLabor, mon: wlLabor, tue: wlLabor, wed: wlLabor, thu: wlLabor, fri: wlLabor, sat: wlLabor, total: 0.00},
			{id: 19, type: "weekly", expense: "Cleaning supplies", sun: cleaning, mon: cleaning, tue: cleaning, wed: cleaning, thu: cleaning, fri: cleaning, sat: cleaning, total: 0.00},
			{id: 20, type: "weekly", expense: "Laundry chemicals", sun: laundry, mon: laundry, tue: laundry, wed: laundry, thu: laundry, fri: laundry, sat: laundry, total: 0.00},
			{id: 21, type: "weekly", expense: "Franchise Fees<sup>1</sup>", sun: fFee, mon: fFee, tue: fFee, wed: fFee, thu: fFee, fri: fFee, sat: fFee, total: fFee*7},
			{id: 22, type: "weekly", expense: "Choice Privileges", sun: wcp, mon: wcp, tue: wcp, wed: wcp, thu: wcp, fri: wcp, sat: wcp, total: wcp*7},
			{id: 23, type: "weekly", expense: "Commission<sup>2</sup>", sun: wcomm, mon: wcomm, tue: wcomm, wed: wcomm, thu: wcomm, fri: wcomm, sat: wcomm, total: wcomm*7},
			{id: 24, type: "weekly", expense: "Credit Card processing<sup>3</sup>", sun: ccFee, mon: ccFee, tue: ccFee, wed: ccFee, thu: ccFee, fri: ccFee, sat: ccFee, total: ccFee*7}
		];

		dispatch(loadInitial(init))
	};

	const submit = () => {
		runSubmit();
	};

	return (
		<Card>
			<CardContent sx={{display: "flex", flexDirection: "column"}}>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Box sx={{p: 2}}>
							<Grid container spacing={0} item xs={12}>
								{inputs.map((chunk, key) =>
									<Fragment key={`chunk_${key}`}>
										{chunk.map((group, groupKey) =>
											<Fragment key={`channel_mix_group_${key}_${groupKey}`}>
												<Grid container spacing={0} item xs={3}>
													{group.map(({ id, label, adornment, value, options }, i) =>
														<Grid key={`item_${key}_${i}`} container spacing={0} sx={{display: "flex", alignItems: "center", mb: 2}}>
															<Grid item xs={6}>
																<Typography sx={{mr: 2}}><b>{label}:</b></Typography>
															</Grid>
															<Grid item xs={6}>
																{!!options ?
																	<TextField
																		sx={{"& input": {textAlign: "end"}}}
																		{...{value, SelectProps}}
																		onChange={(e) => onChange(e, id)}
																		variant="outlined"
																		size="small"
																		fullWidth
																		inputProps={{onFocus: highlightAll}}
																		select
																		disabled={view === "output"}
																	>
																		{!!options && options.map((value, key) =>
																			<MenuItem key={`cost_per_${key}_${groupKey}_${i}_option_${key}`} {...{value}}>{value}</MenuItem>
																		)}
																	</TextField>
																	:
																	<NumericFormat
																		fixedDecimalScale
																		allowLeadingZeros
																		thousandSeparator=","
																		decimalScale={adornment === "$" ? 2 : 0}
																		customInput={TextField}
																		sx={{"& input": {textAlign: "end"}}}
																		{...{value}}
																		onChange={(e) => onChange(e, id)}
																		variant="outlined"
																		size="small"
																		fullWidth
																		InputProps={{
																			startAdornment: !!adornment && <InputAdornment position="start">{adornment}</InputAdornment>
																		}}
																		inputProps={{onFocus: highlightAll}}
																		disabled={view === "output"}
																	/>
																}
															</Grid>
														</Grid>
													)}
												</Grid>
												{groupKey < group.length-1 && <Grid item xs={1} sx={{display: "flex", justifyContent: "center"}}>
													<Divider orientation="vertical"/>
												</Grid>}
											</Fragment>
										)}
										{key === 0 && <Grid item xs={12}>
											<Typography sx={{py: 3}}><i>Additional questions used to calculate the profitability of extended stay business</i></Typography>
										</Grid>}
									</Fragment>
								)}
							</Grid>
						</Box>
					</Grid>
					{view === "input" ?
						<Grid item xs={12} sx={{width: "100%", display: "flex", justifyContent: "center"}}>
							<Button
								disabled={stay === "0"}
								variant="contained"
								onClick={submit}
								sx={{
									p: 3,
									cursor: "pointer",
									display: "flex", alignItems: "center", justifyContent: "center",
									boxShadow: "rgba(46, 125, 50, .2) 0px 0px 10px",
									width: "100%",
									borderRadius: 2
								}}
							>Submit</Button>
						</Grid>
					: null}
				</Grid>
			</CardContent>
		</Card>
	);
};